import { keepPreviousData,useInfiniteQuery } from '@tanstack/react-query';
import {
  type ForwardedRef,
  forwardRef,
  type MouseEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';

import Input from '@/components/Inputs/Input';
import useDropdownPos from '@/hooks/pure/useDropdownPos';
import useKeymap from '@/hooks/pure/useKeymap';
import useDropdownStore from '@/stores/pure/DropdownStore';
import { lowerCaseMatch } from '@/utils/util';

import { CenterFlex, VFlex } from '../base';
import Checkbox from '../Checkbox/Checkbox';
import Spinner from '../Common/Spinner';
import List from '../List/List';
import Typo from '../Typography/Typo';
import SelectPortal from './SelectPortal';
import { Option, OptionList } from './styled';
import type {
  FetcherFunc,
  MultiSelectModalProps,
  MultiSelectOptionT,
} from './types';

type States = {
  dataList: MultiSelectOptionT[];
  info: PaginationInfoT;
  searchValue: string;
  asyncSearchValue: string;
  loading: boolean;
};

const MultiSelectModal = (props: MultiSelectModalProps) => {
  const currDropdown = useDropdownStore((state) => state.currDropdown);
  const setCurrDropdown = useDropdownStore((state) => state.setCurrDropdown);

  const dropdownOpen = useMemo(() => {
    return currDropdown === props.name;
  }, [currDropdown, props.name]);

  const { dropdownPos, setDropdownRef } = useDropdownPos(dropdownOpen, {
    ref: props.anchorRef as RefObject<HTMLDivElement>,
    el: props.triggerEl,
    options: props.modalOptions,
  });

  const close = () => setCurrDropdown(null);

  return (
    <SelectPortal
      status={dropdownOpen}
      style={{
        ...props.style,
        ...dropdownPos,
      }}
      close={close}
    >
      <Dropdown {...props} ref={setDropdownRef} close={close} />
    </SelectPortal>
  );
};

const Dropdown = forwardRef(
  (
    props: MultiSelectModalProps & {
      close: () => void;
    },
    ref?: ForwardedRef<HTMLDivElement>,
  ) => {
    const [state, setState] = useState<States>({
      dataList: [],
      info: {
        count: 0,
        size: 0,
        page: 0,
      },
      searchValue: '',
      asyncSearchValue: '',
      loading: false,
    });

    const [focused, setFocused] = useState<MultiSelectOptionT | null>(null);

    const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading } =
      useInfiniteQuery({
        queryKey: [props.name, state.searchValue],
        queryFn: Array.isArray(props.data)
          ? ({ pageParam }) => {
              const list = props.data as MultiSelectOptionT[];
              return {
                list: list,
                cursor: pageParam,
              };
            }
          : ({ pageParam }) => {
              const fetcher = props.data as FetcherFunc<MultiSelectOptionT>;
              return fetcher({
                cursor: pageParam ?? undefined,
                searchValue: state.searchValue,
              });
            },
        initialPageParam: null as string | null,
        getNextPageParam: (lastPage) => lastPage.cursor,
        placeholderData: keepPreviousData,
      });

    const dataList: MultiSelectOptionT[] = useMemo(() => {
      return data?.pages.flatMap((page) => page.list) || [];
    }, [data?.pages]);

    const currList = useMemo(() => {
      return dataList.filter((option) => {
        return lowerCaseMatch(option.searchName, state.searchValue);
      });
    }, [dataList, state.searchValue]);

    useEffect(() => {
      const curr = currList.findIndex(
        (option) => option.value === focused?.value,
      );
      if (curr === -1 && currList.length) {
        setFocused(currList[0]);
      }
    }, [currList, focused?.value, state.searchValue]);

    const focusNextItem = () => {
      const curr = currList.findIndex(
        (option) => option.value === focused?.value,
      );
      if (curr === -1) return;
      if (curr === currList.length - 1) return;
      setFocused(currList[curr + 1]);
    };

    const focusPrevItem = () => {
      const curr = currList.findIndex(
        (option) => option.value === focused?.value,
      );
      if (curr === -1) return;
      if (curr === 0) return;
      setFocused(currList[curr - 1]);
    };

    useKeymap('ArrowUp', focusPrevItem);

    useKeymap('ArrowDown', focusNextItem);

    useKeymap('Enter', () => {
      if (!focused) return;
      const newArr = props.values.toggle(focused.value);
      props.onCheck?.(newArr, focused.value);
      focused.onClick?.(focused.value);
    });

    return (
      <VFlex>
        <OptionList ref={ref} width={props.width}>
          {props.searchOptions?.enable && (
            <Input
              initFocus
              sizeType="m"
              style={{ width: '100%' }}
              placeholder={props.searchOptions.placeholder || 'Search...'}
              name="searchValue"
              value={state.searchValue}
              onChange={(e) => {
                setState((prev) => ({
                  ...prev,
                  searchValue: e.target.value,
                }));
                props.searchOptions?.onSearch?.(e.target.value);
              }}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.code === 'ArrowUp') {
                  focusPrevItem();
                }
                if (e.code === 'ArrowDown') {
                  focusNextItem();
                }
                if (e.code === 'Enter') {
                  if (!focused) return;
                  const newArr = props.values.toggle(focused.value);
                  props.onCheck?.(newArr, focused.value);
                  focused.onClick?.(focused.value);
                }
                if (e.code === 'Escape') {
                  props.close?.();
                }
              }}
            />
          )}
          <List
            className="gap-0.5"
            data={currList}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isLoading={isLoading}
            isFetchingNextPage={isFetchingNextPage}
            loader={
              <CenterFlex height="200px">
                <Spinner />
              </CenterFlex>
            }
            render={(op, index) => (
              <CheckOptionItem
                key={op.value}
                value={op.value}
                node={props.mapper ? props.mapper(op, index) : op.searchName}
                searchName={op.searchName}
                isFocused={focused?.value === op.value}
                isSelected={props.values.includes(op.value)}
                onCheck={(key, e) =>
                  props.onCheck(props.values.toggle(key), key, e)
                }
              />
            )}
          />
        </OptionList>
        {props.footer}
      </VFlex>
    );
  },
);

type CheckOptionItemProps = MultiSelectOptionT & {
  node: ReactNode;
  isSelected: boolean;
  isFocused: boolean;
  onCheck?: (key: StringNumberValue, e?: MouseEvent) => void;
};

const CheckOptionItem = (props: CheckOptionItemProps) => {
  const onCheckHandler = (e?: MouseEvent) => {
    e?.stopPropagation();
    props.onCheck?.(props.value, e);
    props.onClick?.(props.value, e);
  };

  return (
    <SOption onClick={onCheckHandler} focused={props.isFocused}>
      <Checkbox
        onChange={(_, e) => onCheckHandler(e)}
        checked={props.isSelected}
      />
      <Typo type="label2">{props.node}</Typo>
    </SOption>
  );
};

export const SOption = styled(Option)`
  padding: 4px;
  gap: 4px;
  min-height: 28px;
`;

export default MultiSelectModal;
